/**
 * search.js — Global Search Module for SPEKTRX
 *
 * Fuzzy search across holdings, trades, signals, cashTxs, goals
 * "/" or Cmd+K opens overlay, Escape closes, Arrow keys navigate results
 * Client-side only — zero Supabase risk
 */

import { S } from '../core/state.js';
import { esc, f, ds } from '../core/utils.js';

// ─── FUZZY MATCH ────────────────────────────────────────────────────

/**
 * Simple fuzzy matching — checks if all chars in query appear in text in order.
 * Returns a score (lower = better match) or -1 for no match.
 */
function fuzzyScore(query, text) {
  if (!query || !text) return -1;
  const q = query.toLowerCase();
  const t = text.toLowerCase();

  // Exact substring match — best score
  const subIdx = t.indexOf(q);
  if (subIdx !== -1) return subIdx === 0 ? 0 : 1;

  // Word-start match (e.g., "vo" matches "Vanguard S&P 500 ETF" via "VOO")
  // Check if query matches start of any word
  const words = t.split(/[\s\-_/]+/);
  for (const w of words) {
    if (w.startsWith(q)) return 2;
  }

  // Fuzzy: chars must appear in order
  let qi = 0;
  let gaps = 0;
  let lastMatch = -1;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (lastMatch >= 0) gaps += ti - lastMatch - 1;
      lastMatch = ti;
      qi++;
    }
  }
  if (qi < q.length) return -1; // not all chars matched
  return 3 + gaps; // higher gaps = worse score
}

// ─── INDEX BUILDER ──────────────────────────────────────────────────

/**
 * Build a flat searchable index from current state.
 * Each entry: { type, id, title, subtitle, page, score (set during search) }
 */
export function buildIndex() {
  const idx = [];

  // Holdings (full field names: ticker, name, type, subType, broker, currency)
  (S.holdings || []).filter(h => !h._del).forEach(h => {
    idx.push({
      type: 'holding',
      icon: '◈',
      id: h.id,
      title: h.ticker,
      subtitle: [h.name !== h.ticker ? h.name : '', h.type, h.broker, h.currency].filter(Boolean).join(' · '),
      page: 'portfolio',
      text: [h.ticker, h.name, h.type, h.subType, h.broker, h.currency, h.portfolioName].filter(Boolean).join(' '),
    });
  });

  // Trades (full field names: ticker, type, side, currency, status, note)
  (S.trades || []).filter(t => !t._del).forEach(t => {
    const statusBadge = t.status === 'Open' ? '●' : t.status === 'Partial' ? '◐' : '○';
    idx.push({
      type: 'trade',
      icon: '⇄',
      id: t.id,
      title: `${t.ticker} ${statusBadge} ${t.status}`,
      subtitle: [t.side, t.currency, t.entry ? `@${f(t.entry)}` : ''].filter(Boolean).join(' · '),
      page: 'trades',
      text: [t.ticker, t.type, t.status, t.side, t.currency, t.note].filter(Boolean).join(' '),
    });
  });

  // Signals (uses ticker field, plus assetName, assetClass)
  (S.signals || []).filter(s => !s._del).forEach(s => {
    idx.push({
      type: 'signal',
      icon: '⚡',
      id: s.id,
      title: s.ticker || s.assetName || 'Signal',
      subtitle: [s.action, s.source, s.assetClass].filter(Boolean).join(' · '),
      page: 'signals',
      text: [s.ticker, s.assetName, s.assetClass, s.action, s.source, s.trend].filter(Boolean).join(' '),
    });
  });

  // Cash Transactions (full field names: type, amount, currency, date, reason, note, cat)
  (S.cashTxs || []).filter(c => !c._del).slice(0, 200).forEach(c => {
    idx.push({
      type: 'cash',
      icon: '$',
      id: c.id,
      title: `${c.type} ${c.currency || 'USD'} ${c.amount ? (c.amount >= 0 ? '+' : '') + f(c.amount) : ''}`,
      subtitle: [c.reason, c.cat, c.date ? ds(c.date) : ''].filter(Boolean).join(' · '),
      page: 'cash',
      text: [c.type, c.reason, c.note, c.cat, c.currency].filter(Boolean).join(' '),
    });
  });

  // Goals (full field names: name, type, targetAmount, currency, status)
  (S.goals || []).filter(g => !g._del).forEach(g => {
    idx.push({
      type: 'goal',
      icon: '◎',
      id: g.id,
      title: g.name,
      subtitle: [g.status, g.currency, g.deadline ? `by ${ds(g.deadline)}` : ''].filter(Boolean).join(' · '),
      page: 'goals',
      text: [g.name, g.currency, g.status, g.type].filter(Boolean).join(' '),
    });
  });

  // Bonus Plans
  (S.bonusPlans || []).filter(b => !b._del).forEach(b => {
    idx.push({
      type: 'bonus',
      icon: '★',
      id: b.id,
      title: b.name || 'Bonus Plan',
      subtitle: `FY${b.fiscalYear || ''}`,
      page: 'bonus',
      text: [b.name, b.company, `FY${b.fiscalYear}`].filter(Boolean).join(' '),
    });
  });

  // Income Definitions
  (S.incomeDefs || []).filter(d => !d._del).forEach(d => {
    idx.push({
      type: 'income',
      icon: '↓',
      id: d.id,
      title: d.name || 'Income',
      subtitle: [d.type, d.active ? 'Active' : 'Inactive'].filter(Boolean).join(' · '),
      page: 'cashflow',
      text: [d.name, d.type, 'income'].filter(Boolean).join(' '),
    });
  });

  // Expense Definitions
  (S.expenseDefs || []).filter(d => !d._del).forEach(d => {
    idx.push({
      type: 'expense',
      icon: '↑',
      id: d.id,
      title: d.name || 'Expense',
      subtitle: [d.type, d.active !== false ? 'Active' : 'Inactive'].filter(Boolean).join(' · '),
      page: 'cashflow',
      text: [d.name, d.type, 'expense', d.note].filter(Boolean).join(' '),
    });
  });

  return idx;
}

// ─── SEARCH FUNCTION ────────────────────────────────────────────────

/**
 * Search the index with a query string. Returns grouped results.
 * @param {string} query - User input
 * @param {Array} index - From buildIndex()
 * @param {number} limit - Max results per type
 * @returns {{ type: string, label: string, items: Array }[]}
 */
export function search(query, index, limit = 5) {
  if (!query || query.length < 1) return [];

  // Score each item
  const scored = index.map(item => ({
    ...item,
    score: fuzzyScore(query, item.text),
  })).filter(i => i.score >= 0);

  // Sort by score (lower = better)
  scored.sort((a, b) => a.score - b.score);

  // Group by type
  const typeLabels = {
    holding: 'Holdings',
    trade: 'Trades',
    signal: 'Signals',
    cash: 'Cash Transactions',
    goal: 'Goals',
    bonus: 'Bonus',
    income: 'Income Definitions',
    expense: 'Expense Definitions',
  };
  const typeOrder = ['holding', 'trade', 'signal', 'goal', 'cash', 'bonus', 'income', 'expense'];

  const groups = {};
  for (const item of scored) {
    if (!groups[item.type]) groups[item.type] = [];
    if (groups[item.type].length < limit) {
      groups[item.type].push(item);
    }
  }

  return typeOrder
    .filter(t => groups[t] && groups[t].length > 0)
    .map(t => ({ type: t, label: typeLabels[t] || t, items: groups[t] }));
}

// ─── SEARCH UI ──────────────────────────────────────────────────────

let _overlay = null;
let _input = null;
let _resultsList = null;
let _index = [];
let _activeIdx = -1;
let _flatResults = [];

function ensureOverlay() {
  if (_overlay) return;

  _overlay = document.createElement('div');
  _overlay.id = 'searchOverlay';
  _overlay.className = 'search-overlay';
  _overlay.innerHTML = `
    <div class="search-box" role="dialog" aria-modal="true" aria-label="Global Search">
      <div class="search-input-row">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--t3)" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input type="text" id="globalSearchInput" placeholder="Search holdings, trades, signals, goals..." autocomplete="off" spellcheck="false" />
        <kbd class="search-kbd">Esc</kbd>
      </div>
      <div id="searchResults" class="search-results" role="listbox"></div>
      <div class="search-footer">
        <span><kbd>↑↓</kbd> Navigate</span>
        <span><kbd>↵</kbd> Go</span>
        <span><kbd>Esc</kbd> Close</span>
      </div>
    </div>
  `;
  document.body.appendChild(_overlay);

  _input = _overlay.querySelector('#globalSearchInput');
  _resultsList = _overlay.querySelector('#searchResults');

  // Backdrop click closes
  _overlay.addEventListener('click', (e) => {
    if (e.target === _overlay) closeSearch();
  });

  // Input handler — debounced
  let _debounce = null;
  _input.addEventListener('input', () => {
    clearTimeout(_debounce);
    _debounce = setTimeout(() => renderResults(_input.value.trim()), 80);
  });

  // Keyboard navigation
  _input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      e.preventDefault();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectActive();
    }
  });
}

function renderResults(query) {
  if (!_resultsList) return;
  _activeIdx = -1;
  _flatResults = [];

  if (!query) {
    _resultsList.innerHTML = '<div class="search-empty">Type to search across all data...</div>';
    return;
  }

  const groups = search(query, _index);

  if (groups.length === 0) {
    _resultsList.innerHTML = '<div class="search-empty">No results</div>';
    return;
  }

  let html = '';
  for (const group of groups) {
    html += `<div class="search-group-label">${esc(group.label)}</div>`;
    for (const item of group.items) {
      const flatIdx = _flatResults.length;
      _flatResults.push(item);
      html += `<div class="search-item" data-flat-idx="${flatIdx}" role="option">
        <span class="search-icon">${item.icon}</span>
        <div class="search-item-text">
          <div class="search-item-title">${esc(item.title)}</div>
          <div class="search-item-sub">${esc(item.subtitle)}</div>
        </div>
        <span class="search-item-type">${esc(group.label)}</span>
      </div>`;
    }
  }
  _resultsList.innerHTML = html;

  // Click handler on results
  _resultsList.querySelectorAll('.search-item').forEach(el => {
    el.addEventListener('click', () => {
      const idx = parseInt(el.dataset.flatIdx, 10);
      if (_flatResults[idx]) navigateTo(_flatResults[idx]);
    });
  });

  // Auto-select first
  if (_flatResults.length > 0) {
    _activeIdx = 0;
    updateActiveHighlight();
  }
}

function moveActive(dir) {
  if (_flatResults.length === 0) return;
  _activeIdx += dir;
  if (_activeIdx < 0) _activeIdx = _flatResults.length - 1;
  if (_activeIdx >= _flatResults.length) _activeIdx = 0;
  updateActiveHighlight();
}

function updateActiveHighlight() {
  if (!_resultsList) return;
  _resultsList.querySelectorAll('.search-item').forEach((el, i) => {
    el.classList.toggle('active', i === _activeIdx);
    if (i === _activeIdx) el.scrollIntoView({ block: 'nearest' });
  });
}

function selectActive() {
  if (_activeIdx >= 0 && _activeIdx < _flatResults.length) {
    navigateTo(_flatResults[_activeIdx]);
  }
}

function navigateTo(item) {
  closeSearch();

  // Navigate to the page (use grouped nav activator)
  if (window._navActivateTab) {
    window._navActivateTab(item.page);
  } else {
    const tab = document.querySelector(`[data-page="${item.page}"]`);
    if (tab) tab.click();
  }

  // After a short delay, try to highlight/scroll to the item
  setTimeout(() => {
    // For holdings and trades, try to find the row with matching id
    const el = document.querySelector(`[data-id="${item.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('search-highlight');
      setTimeout(() => el.classList.remove('search-highlight'), 2000);
    }
  }, 200);
}

// ─── PUBLIC API ─────────────────────────────────────────────────────

export function openSearch() {
  ensureOverlay();
  _index = buildIndex();
  _overlay.classList.add('active');
  _input.value = '';
  _resultsList.innerHTML = '<div class="search-empty">Type to search across all data...</div>';
  _activeIdx = -1;
  _flatResults = [];

  // Focus input after animation
  requestAnimationFrame(() => _input.focus());
}

export function closeSearch() {
  if (_overlay) _overlay.classList.remove('active');
}

export function isSearchOpen() {
  return _overlay && _overlay.classList.contains('active');
}
